import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
  }

  .area {
    opacity: 0.6;
  }

  .line {
    fill: none;
    stroke-width: 2px;
  }

  .axis text {
    font-family: 'Segoe UI', sans-serif;
    font-size: 12px;
  }

  .axis-label {
    font-family: 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
  }

  .grid line {
    stroke: #e0e0e0;
    stroke-opacity: 0.7;
    shape-rendering: crispEdges;
  }

  .grid path {
    stroke-width: 0;
  }

  .legend {
    font-family: 'Segoe UI', sans-serif;
    font-size: 12px;
  }
`;

/**
 * D3 Area Chart Component
 * @param {Array} data - Array of objects with format: [{x: value, y1: value, y2: value, ...}]
 * @param {String} xKey - Key for x-axis values
 * @param {Array} yKeys - Array of keys for y-axis values (supports multiple areas)
 * @param {String} xLabel - Label for x-axis
 * @param {String} yLabel - Label for y-axis
 * @param {Array} colors - Array of colors for each area
 * @param {Array} labels - Array of labels for legend
 * @param {String} height - Chart height (default: 400px)
 * @param {Boolean} showGrid - Show grid lines (default: true)
 * @param {Boolean} stacked - Stack areas on top of each other (default: false)
 */
const D3AreaChart = ({ 
  data = [], 
  xKey = 'x', 
  yKeys = ['y'], 
  xLabel = '', 
  yLabel = '',
  colors = ['#0077B5', '#27AE60'],
  labels = null,
  height = '400px',
  showGrid = true,
  stacked = false
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: parseInt(height) });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 20, right: labels ? 120 : 30, bottom: 60, left: 60 };
    const width = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Determine if x-axis is date or numeric
    const isDate = data[0][xKey] instanceof Date || !isNaN(Date.parse(data[0][xKey]));
    
    const x = isDate
      ? d3.scaleTime()
          .domain(d3.extent(data, d => new Date(d[xKey])))
          .range([0, width])
      : d3.scaleLinear()
          .domain([0, d3.max(data, d => d[xKey])])
          .range([0, width]);

    // Calculate max y value
    let maxY = 0;
    if (stacked) {
      maxY = d3.max(data, d => d3.sum(yKeys, key => d[key] || 0));
    } else {
      maxY = d3.max(data, d => d3.max(yKeys, key => d[key] || 0));
    }

    const y = d3.scaleLinear()
      .domain([0, maxY * 1.1])
      .range([chartHeight, 0]);

    // Add grid
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat('')
        );
    }

    // X Axis
    const xAxis = isDate 
      ? d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat('%b %Y'))
      : d3.axisBottom(x).ticks(5);

    g.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Y Axis
    g.append('g')
      .attr('class', 'axis')
      .call(d3.axisLeft(y).ticks(5));

    // Create stacked data if needed
    let stackedData = data;
    if (stacked && yKeys.length > 1) {
      const stack = d3.stack()
        .keys(yKeys)
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);
      
      stackedData = stack(data);
    }

    // Draw areas
    yKeys.forEach((yKey, index) => {
      const areaData = stacked && yKeys.length > 1 ? stackedData[index] : data;
      const color = colors[index % colors.length];

      const area = stacked && yKeys.length > 1
        ? d3.area()
            .x(d => x(isDate ? new Date(d.data[xKey]) : d.data[xKey]))
            .y0(d => y(d[0]))
            .y1(d => y(d[1]))
            .curve(d3.curveMonotoneX)
        : d3.area()
            .x(d => x(isDate ? new Date(d[xKey]) : d[xKey]))
            .y0(chartHeight)
            .y1(d => y(d[yKey] || 0))
            .curve(d3.curveMonotoneX);

      // Draw area
      g.append('path')
        .datum(areaData)
        .attr('class', 'area')
        .attr('d', area)
        .attr('fill', color)
        .style('opacity', 0)
        .transition()
        .duration(800)
        .style('opacity', 0.6);

      // Draw line on top
      const line = stacked && yKeys.length > 1
        ? d3.line()
            .x(d => x(isDate ? new Date(d.data[xKey]) : d.data[xKey]))
            .y(d => y(d[1]))
            .curve(d3.curveMonotoneX)
        : d3.line()
            .x(d => x(isDate ? new Date(d[xKey]) : d[xKey]))
            .y(d => y(d[yKey] || 0))
            .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(areaData)
        .attr('class', 'line')
        .attr('d', line)
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('fill', 'none')
        .style('opacity', 0)
        .transition()
        .duration(800)
        .style('opacity', 1);
    });

    // Add interactive overlay for tooltips
    const bisect = d3.bisector(d => isDate ? new Date(d[xKey]) : d[xKey]).left;

    const focus = g.append('g')
      .style('display', 'none');

    focus.append('line')
      .attr('class', 'focus-line')
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .attr('y1', 0)
      .attr('y2', chartHeight);

    g.append('rect')
      .attr('class', 'overlay')
      .attr('width', width)
      .attr('height', chartHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mouseover', () => focus.style('display', null))
      .on('mouseout', () => {
        focus.style('display', 'none');
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      })
      .on('mousemove', function(event) {
        const [mouseX] = d3.pointer(event);
        const x0 = x.invert(mouseX);
        const i = bisect(data, x0, 1);
        const d0 = data[i - 1];
        const d1 = data[i];
        const d = d1 && x0 - (isDate ? new Date(d0[xKey]) : d0[xKey]) > (isDate ? new Date(d1[xKey]) : d1[xKey]) - x0 ? d1 : d0;

        if (d) {
          focus.select('.focus-line')
            .attr('x1', x(isDate ? new Date(d[xKey]) : d[xKey]))
            .attr('x2', x(isDate ? new Date(d[xKey]) : d[xKey]));

          if (tooltipRef.current) {
            const xValue = isDate ? d3.timeFormat('%b %d, %Y')(new Date(d[xKey])) : d[xKey];
            let html = `<strong>${xLabel || 'X'}: ${xValue}</strong><br/>`;
            yKeys.forEach((yKey, index) => {
              const label = labels ? labels[index] : yKey;
              html += `${label}: ${(d[yKey] || 0).toLocaleString()}<br/>`;
            });
            
            d3.select(tooltipRef.current)
              .style('opacity', 1)
              .html(html)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 20}px`);
          }
        }
      });

    // X Axis Label
    if (xLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', chartHeight + margin.bottom - 10)
        .text(xLabel);
    }

    // Y Axis Label
    if (yLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('text-anchor', 'middle')
        .attr('transform', 'rotate(-90)')
        .attr('x', -chartHeight / 2)
        .attr('y', -margin.left + 15)
        .text(yLabel);
    }

    // Legend
    if (labels && yKeys.length > 1) {
      const legend = svg.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${margin.left + width + 10},${margin.top})`);

      yKeys.forEach((yKey, index) => {
        const legendItem = legend.append('g')
          .attr('transform', `translate(0,${index * 25})`);

        legendItem.append('rect')
          .attr('width', 18)
          .attr('height', 18)
          .attr('fill', colors[index % colors.length]);

        legendItem.append('text')
          .attr('x', 24)
          .attr('y', 9)
          .attr('dy', '0.35em')
          .style('font-size', '12px')
          .text(labels[index]);
      });
    }
  }, [data, dimensions, xKey, yKeys, xLabel, yLabel, colors, labels, showGrid, stacked]);

  return (
    <ChartContainer ref={containerRef} height={height}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef} className="tooltip"></div>
    </ChartContainer>
  );
};

export default D3AreaChart;


